/**
 * ContributorBadge — bronze/silver/gold/platinum tier pill for a
 * community reporter. Two shapes:
 *   • "chip"    → inline strip, e.g. next to author name on story pages
 *   • "widget"  → bigger card with progress to the next tier
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Compass, Map as MapIcon, Feather } from "lucide-react";
import { npApi } from "@/pages/newspulse/api";

const ICONS = { sparkles: Sparkles, compass: Compass, map: MapIcon, feather: Feather };

function TierPill({ tier, size = "sm", testId }) {
  if (!tier) return null;
  const Icon = ICONS[tier.emoji_free_icon || "sparkles"] || Sparkles;
  const pad = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-0.5 text-[11px]";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-wider ${pad}`}
      style={{ color: tier.color, background: tier.bg, borderColor: tier.color + "40" }}
      data-testid={testId}
    >
      <Icon className={size === "lg" ? "w-3.5 h-3.5" : "w-3 h-3"} />
      {tier.label}
    </span>
  );
}

export default function ContributorBadge({ userId, shape = "chip", className = "", initial = null }) {
  const [data, setData] = useState(initial);

  useEffect(() => {
    if (initial) return;
    if (!userId) { npApi.myBadge().then(setData).catch(() => {}); return; }
    npApi.badgeForUser(userId).then(setData).catch(() => {});
  }, [userId, initial]);

  if (!data || !data.tier) {
    if (shape === "widget") {
      return (
        <div className={`rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500 ${className}`}
             data-testid="contributor-badge-empty">
          Report your first story to earn the <strong className="text-slate-700">Community Reporter</strong> badge.
        </div>
      );
    }
    return null;
  }

  if (shape === "widget") {
    const progress = data.next_tier
      ? Math.min(100, Math.round((data.approved_count / data.next_tier.min) * 100))
      : 100;
    return (
      <div className={`rounded-2xl border p-4 ${className}`}
           style={{ background: data.tier.bg, borderColor: data.tier.color + "40" }}
           data-testid="contributor-badge-widget">
        <div className="flex items-center gap-3 mb-2">
          <TierPill tier={data.tier} size="lg" testId="contributor-badge-widget-pill" />
          <span className="text-sm text-slate-700 font-semibold">
            {data.approved_count} published {data.approved_count === 1 ? "story" : "stories"}
          </span>
        </div>
        {data.next_tier && (
          <>
            <div className="flex items-center gap-2 text-[11px] text-slate-700">
              <span>Next: <strong>{data.next_tier.label}</strong></span>
              <span className="ml-auto text-slate-500">{data.approved_count} / {data.next_tier.min}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-white/60 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${progress}%`, background: data.tier.color }} />
            </div>
          </>
        )}
      </div>
    );
  }
  // Chip — links to reporter public profile when a userId is passed.
  const pill = <TierPill tier={data.tier} testId="contributor-badge-chip" />;
  if (userId) {
    return <Link to={`/reporter/${userId}`} className="hover:opacity-80 transition-opacity" data-testid="contributor-badge-link">{pill}</Link>;
  }
  return pill;
}
